import React, { useCallback, useRef } from 'react';
import {
  ScrollView,
  TouchableOpacity,
  useWindowDimensions,
} from 'react-native';

import { MyTasksTab } from '@components/MyTasks/myTasksGrouping';
import { Box, Text } from '@components/Restyle';
import Icon from '@components/shared/Icon/Icon';
import Images from '@themes/images';
import theme, { getScaledSize, SCALED_DIMENSIONS } from '@themes/theme';
type TabConfig = {
  key: MyTasksTab;
  label: string;
  icon: keyof typeof Images;
};

const TABS: TabConfig[] = [
  { key: MyTasksTab.MyDay, label: 'My Day', icon: 'Sun' },
  { key: MyTasksTab.SevenDays, label: '7 Days', icon: 'Calendar' },
  { key: MyTasksTab.AllTasks, label: 'All Tasks', icon: 'ListTodo' },
];

type MyTasksTabBarProps = {
  activeTab: MyTasksTab;
  onTabPress: (tab: MyTasksTab) => void;
  onFilterPress: () => void;
  showDot?: boolean;
};

// Filter button box is 32 wide, plus its own ml (s) and mr (m) margins —
// the full width it reserves at the end of the tab bar, not just the icon
// box itself. Undercounting this left the last few px of "All Tasks"
// still hidden behind the filter button after the auto-scroll (AC11).
const FILTER_BUTTON_WIDTH = theme.spacing.s + 32 + theme.spacing.m;

/**
 * Fixed chip-style tab bar for the My Tasks revamp (TA-10186 AC1/AC7/AC11).
 * When a partially visible tab is tapped, the bar auto-scrolls so the tab
 * becomes fully visible (AC11).
 */
export const MyTasksTabBar: React.FC<MyTasksTabBarProps> = ({
  activeTab,
  onTabPress,
  onFilterPress,
  showDot = false,
}) => {
  const scrollRef = useRef<ScrollView>(null);
  const tabLayoutsRef = useRef<Record<string, { x: number; width: number }>>(
    {}
  );
  const scrollOffsetRef = useRef(0);
  const { width: windowWidth } = useWindowDimensions();

  const handleTabPress = useCallback(
    (tab: MyTasksTab) => {
      const isLastTab = tab === TABS[TABS.length - 1].key;
      if (isLastTab) {
        // Scrolling to the exact computed offset for the last tab is
        // sensitive to onLayout rounding, leaving a sliver still hidden
        // behind the filter button. scrollToEnd always lands flush with
        // the scroll content's true right edge, so use it whenever the
        // last tab is the target.
        scrollRef.current?.scrollToEnd({ animated: true });
      } else {
        const layout = tabLayoutsRef.current[tab];
        if (layout) {
          const visibleWidth = windowWidth - FILTER_BUTTON_WIDTH;
          const offset = scrollOffsetRef.current;
          if (layout.x < offset) {
            scrollRef.current?.scrollTo({ x: layout.x, animated: true });
          } else if (layout.x + layout.width > offset + visibleWidth) {
            scrollRef.current?.scrollTo({
              x: layout.x + layout.width - visibleWidth,
              animated: true,
            });
          }
        }
      }
      onTabPress(tab);
    },
    [onTabPress, windowWidth]
  );

  return (
    <Box
      flexDirection='row'
      alignItems='center'
      backgroundColor='white'
      pb='m'
      borderBottomColor='grey02'
      borderBottomWidth={1}>
      <ScrollView
        ref={scrollRef}
        horizontal
        showsHorizontalScrollIndicator={false}
        onScroll={(e) => {
          scrollOffsetRef.current = e.nativeEvent.contentOffset.x;
        }}
        scrollEventThrottle={16}
        // Switching the active tab swaps its text variant, which changes the
        // chip width after the press-time scrollToEnd already ran. On iOS
        // (narrow screens) that left "All Tasks" partly hidden, so re-pin
        // to the end once the new content size is known.
        onContentSizeChange={() => {
          if (activeTab === TABS[TABS.length - 1].key) {
            scrollRef.current?.scrollToEnd({ animated: true });
          }
        }}
        contentContainerStyle={{ paddingLeft: theme.spacing.m }}>
        {TABS.map((tab, index) => {
          const isActive = tab.key === activeTab;
          return (
            <TouchableOpacity
              key={tab.key}
              testID={`my-tasks-tab-${tab.key}`}
              accessibilityLabel={tab.label}
              accessibilityState={{ selected: isActive }}
              // Active/inactive is already fully communicated by isActive's
              // own background/border/icon-color/text-variant styling below
              // — the default activeOpacity dim-on-press is redundant on top
              // of that, and it's exactly what gets visibly stuck faded on
              // the just-tapped tab (TA-10187) when the JS thread is busy
              // enough with the tab switch's (deferred, but still real) work
              // to delay the native press-release event that resets it.
              // Disabling it removes that failure mode outright instead of
              // depending on the switch staying cheap enough to never stall.
              activeOpacity={1}
              onLayout={(e) => {
                tabLayoutsRef.current[tab.key] = {
                  x: e.nativeEvent.layout.x,
                  width: e.nativeEvent.layout.width,
                };
              }}
              onPress={() => handleTabPress(tab.key)}>
              <Box
                flexDirection='row'
                alignItems='center'
                marginRight={index === TABS.length - 1 ? 'none' : 'xs'}
                paddingHorizontal='s'
                paddingVertical='xxs'
                borderRadius='l'
                height={32}
                borderWidth={1}
                borderColor={isActive ? 'grey02' : 'grey01'}
                backgroundColor={isActive ? 'white' : 'grey01'}>
                <Icon
                  name={tab.icon}
                  variant='s'
                  color={isActive ? 'greenSecondary' : 'textPrimary'}
                />
                <Text
                  marginLeft='xxs'
                  variant={isActive ? 'webBody' : 'bodySecondaryNew'}
                  color='textSecondary'>
                  {tab.label}
                </Text>
              </Box>
            </TouchableOpacity>
          );
        })}
      </ScrollView>
      <Box
        width={32}
        height={32}
        borderWidth={1}
        ml='s'
        mr='m'
        justifyContent='center'
        alignItems='center'
        borderRadius='m'
        borderColor='grey02'>
        {showDot && (
          <Box
            borderRadius='xs'
            backgroundColor='greenSecondary'
            style={{
              width: getScaledSize(SCALED_DIMENSIONS.FILTER_DOT),
              height: getScaledSize(SCALED_DIMENSIONS.FILTER_DOT),
              position: 'absolute',
              top: getScaledSize(-1),
              right: getScaledSize(0),
            }}></Box>
        )}

        <Icon
          name='Sliders'
          variant='s'
          color='black'
          accessibilityLabel='Filter tasks'
          onPress={onFilterPress}
          // Icon itself renders at 16px (variant='s') — well under the
          // 44pt/48dp minimum touch target recommended by iOS/Android.
          // hitSlop only expands the invisible hit-test area, not the
          // visible 32x32 box, so this doesn't change anything on screen.
          hitSlop={{
            top: theme.spacing.m,
            bottom: theme.spacing.m,
            left: theme.spacing.m,
            right: theme.spacing.m,
          }}
        />
      </Box>
    </Box>
  );
};
